import { 
    Card,
    CardHeader,
    CardTitle,
    CardContent,
    CardFooter,
    CardDescription
} from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { CartesianGrid, Line, LineChart, Pie, PieChart, XAxis} from "recharts";
import { cn } from "@/lib/utils";
import { LucideProps
    ,DollarSign,
    BadgeDollarSign,
    Truck,
    TrendingUp,
} from "lucide-react";
import React, { forwardRef } from "react";
import Amount from "utils/amount";
import { Skeleton } from "@/components/ui/skeleton";

interface SavingKPIrops extends React.HTMLAttributes<HTMLDivElement> {
    totalPurchase?:number,
    deliveryTotal?:number,
    totalSaving?:number,
    deprecatedValues?:boolean
}

export const KPIDashBoard = forwardRef<HTMLDivElement,SavingKPIrops>(({totalPurchase,deliveryTotal,totalSaving,...props},ref)=>{
    return (
            <div className={cn("flex flex-row flex-wrap gap-2 justify-start mx-auto md:mx-0",props.className)}>
            <KPI   
                className={cn("w-40 h-36 flex-1 max-w-40",{"line-through":props.deprecatedValues})}
                title="Tú compra"
                // subTitle=" + 1% de última compra."
                icon={DollarSign}
                amount={totalPurchase??0}                
            />
            <KPI
                className={cn("w-40 h-36",{"line-through":props.deprecatedValues})}
                title="Despacho"
                subTitle=""
                icon={Truck}
                amount={deliveryTotal??0}                
            />            
            <KPI
                className={cn("w-40 h-36 flex-1 max-w-40",{"line-through":props.deprecatedValues})}
                title="Total Ahorro"
                subTitle=""
                icon={BadgeDollarSign}
                amount={totalSaving??0}     
                priceClass="best-price"           
            />                    
        </div>
    );
})

interface KPIDashBoardSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {

}

export const KPIDashBoardSkeleton:React.FC<KPIDashBoardSkeletonProps> = (props) => {
    return(
        <div className={cn("flex flex-row flex-wrap gap-2 justify-start",props.className)}>
            <Skeleton className="h-36 w-48" />
            <Skeleton className="h-36 w-48" />
            <Skeleton className="h-36 w-48" />
        </div>
    );
}

interface KPIProps extends React.HTMLAttributes<HTMLDivElement> {
    title:string,
    subTitle?:React.ReactNode,
    subText?:string,
    amount:number,
    icon: React.FC<LucideProps>,
    priceClass?:string,
    iconClassName?:string
}

interface KPIBaseContentProps extends React.HTMLAttributes<HTMLDivElement> {
    title:string,
    children:React.ReactNode,
    icon: React.FC<LucideProps>,
    iconClassName?:string
}


export const KPI = forwardRef<HTMLDivElement,KPIProps>(({title,icon:Icon,amount,...props},ref)=>{
    return (
        <Card className={cn("gap-2 pb-4",props.className)}>
            <CardHeader className={"flex flex-row items-center justify-between space-y-0 gap-2"} >
                <CardTitle className="font-bold">{title}</CardTitle>
                <Icon className={cn("h-6 w-6 text-muted-foreground",props.iconClassName??"")} />
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
                <div className={cn("text-3xl md:text-4xl font-bold",props.priceClass)}>
                    <Amount
                        amount={amount??0} 
                        currency="CLP" 
                        locale="es-CL" 
                        />
                        {
                          (props.subText)&&<span className="text-base font-normal ml-2">{props.subText}</span>
                        }
                </div>
                <div className="text-muted-foreground">{props.subTitle}</div>
            </CardContent>
        </Card>         
    );
})

export const KPIBase = forwardRef<HTMLDivElement,KPIBaseContentProps>(({title,icon:Icon,...props},ref)=>{
    return (
        <Card className={cn("gap-2 pb-4",props.className)}>
            <CardHeader className={"flex flex-row items-center justify-between space-y-0 gap-2"} >
                <CardTitle className="font-bold">{title}</CardTitle>
                <Icon className={cn("h-6 w-6 text-muted-foreground",props.iconClassName??"")} />
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
                <div className="text-muted-foreground">{props.children}</div>
            </CardContent>
        </Card>         
    );
})

export interface ChartDatum {
    month: string,
    display_month: string,
    [key: string]: number | string;
}

interface LineChartProps extends React.HTMLAttributes<HTMLDivElement> {
    title:string,
    subTitle?:string,
    chartData:ChartDatum[]
}



export const LineChartKPI = forwardRef<HTMLDivElement,LineChartProps>(({title,subTitle,...props},ref)=>{

    let colorIndex = 0;
    const colors = ["hsl(var(--chart-1))", "hsl(var(--chart-2))","hsl(var(--chart-3))",
                    "hsl(var(--chart-4))","hsl(var(--chart-5))","hsl(var(--chart-6))",
                    "hsl(var(--chart-7))","hsl(var(--chart-8))","hsl(var(--chart-9))"];
    const chartConfig: { [key: string]: { label: string; color: string } }   = {} satisfies ChartConfig
    props.chartData?.forEach((month,index) => {        
        Object.keys(month).forEach((key) => {         
            if(key !== "month" && key !== "display_month" && !chartConfig[key]){
                chartConfig[key] = {
                    label:key,
                    color:colors[colorIndex]
                }
                colorIndex++;
            }
        });
    }
    );
    
    return (
        <Card>
            <CardHeader>
            <CardTitle  className="text-xs md:text-sm font-medium">{title}</CardTitle>
            <CardDescription className="text-xs">{subTitle}</CardDescription>
            </CardHeader>
            <CardContent>
            <ChartContainer config={chartConfig}>
                <LineChart
                accessibilityLayer
                data={props.chartData}
                margin={{
                    left: 12,
                    right: 12,
                    top:12
                }}
                >
                <CartesianGrid vertical={true} />
                <XAxis
                    dataKey="display_month"
                    tickLine={true}
                    axisLine={false}
                    minTickGap={32}
                    tickMargin={10}
                    tickFormatter={(value) => value.slice(0, 8)}
                />
                <ChartTooltip cursor={true}
                    content={
                    <ChartTooltipContent indicator="line"
                    formatter={(value,name, payload) => {
                        return (
                        <div className="flex w-full flex-row justify-between gap-4">
                            <div className="flex flex-row items-center gap-2 ">                    
                                <div
                                className={cn(
                                    "shrink-0 rounded-[2px] border-[--color-border] bg-[--color-bg]",
                                    {
                                        "h-2.5 w-2.5":true,
                                    }
                                )}
                                style={
                                    {
                                    "--color-bg": payload.color,
                                    "--color-border": payload.color,
                                    } as React.CSSProperties
                                }
                                />                   
                                <span className="text-muted-foreground">{name}</span>
                            </div>
                            
                            <span className="font-semibold">
                                <Amount 
                                    amount={Number(value?.toString() ?? "0")} 
                                    currency="CLP" 
                                    locale="es-CL" 
                                />
                            </span>
                        </div>)
                        // return `${name} $ ${value.toLocaleString()}`
                    }}/>} />                
            {
            Object.keys(chartConfig).map((key,index) => {
                return(
                    <Line
                        key={index}
                        dataKey={key}
                        type ="monotone"
                        stroke={chartConfig[key].color}
                        strokeWidth={2}
                        dot={true}
                    />
                );
            }
            )}
                </LineChart>
            </ChartContainer>
            </CardContent>
            <CardFooter>
            {/* <div className="flex w-full items-start gap-2 text-sm">
                <div className="grid gap-2">
                <div className="flex items-center gap-2 font-medium leading-none">
                    Trending up by 5.2% this month <TrendingUp className="h-4 w-4" />
                </div>
                <div className="flex items-center gap-2 leading-none text-muted-foreground">
                    Showing total visitors for the last 6 months
                </div>
                </div>
            </div> */}
            </CardFooter>
        </Card>
    );
})


interface PieChartKPIProps extends React.HTMLAttributes<HTMLDivElement> {
    title:string,
    subtitle?:string,
    icon: React.FC<LucideProps>
}

export const PieChartKPI = forwardRef<HTMLDivElement,PieChartKPIProps>(({title,icon:Icon,...props},ref)=>{
    const chartData = [
        { browser: "chrome", visitors: 275, fill: "var(--color-chrome)" },
        { browser: "safari", visitors: 200, fill: "var(--color-safari)" },
        { browser: "firefox", visitors: 187, fill: "var(--color-firefox)" },
        { browser: "edge", visitors: 173, fill: "var(--color-edge)" },
        { browser: "other", visitors: 90, fill: "var(--color-other)" },
    ];

    const chartConfig = {
        visitors: {
            label: "Visitors",
        },
        chrome: {
            label: "Chrome",
            color: "hsl(var(--chart-1))",
        },
        safari: {
            label: "Safari",
            color: "hsl(var(--chart-2))",
        },
        firefox: {
            label: "Firefox",
            color: "hsl(var(--chart-3))",
        },
        edge: {
            label: "Edge",
            color: "hsl(var(--chart-4))",
        },
        other: {
            label: "Other",
            color: "hsl(var(--chart-5))",
        },
    } satisfies ChartConfig;
    
    return(
    <Card className={cn("flex flex-col",props.className)}>
        <CardHeader className={"flex flex-row items-center justify-between space-y-0 gap-2 pb-0"}>
            <CardTitle className="text-xs md:text-sm font-medium">{title}</CardTitle>
            <Icon className="h-4 w-4 text-muted-foreground" />
            {/* <CardDescription>{props.subtitle}</CardDescription> */}
        </CardHeader>
        <CardContent className="flex-1 pb-0">
            <ChartContainer
            config={chartConfig}
            className="aspect-square w-full max-h-20"
            >
            <PieChart className="flex flex-row">
                <Pie data={chartData} dataKey="visitors" />
                <ChartTooltip
                    cursor={true}
                    content={<ChartTooltipContent hideLabel />}
            />
            </PieChart>
            </ChartContainer>
        </CardContent>
    </Card>        
    );
});
